import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Tooltip,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Dashboard as DashboardIcon,
  ListAlt as TransactionsIcon,
  BarChart as ReportsIcon,
  TrackChanges as MilestonesIcon,
  Logout as LogoutIcon,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SavingsIcon from '@mui/icons-material/Savings';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLES, type RoleType } from '../../constants/roles';
type NavItem = {
  label: string;
  icon: React.ReactNode;
  path: string;
  roles: RoleType[];
  indent?: boolean;
};

type SideBarProps = {
  mobileOpen: boolean;
  onMobileClose: () => void;
};

const Sidebar: React.FC<SideBarProps> = ({ mobileOpen, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { auth, logout } = useAuth(); // ✅ Grab logout function

  const userRole = auth.user?.role as RoleType | undefined;
  const isSuperAdmin = userRole === ROLES.SUPER_ADMIN;
  const isNotOnboarded = auth.user?.hasCompletedOnboarding === false;
  const shouldShowDashboardOnly = !isSuperAdmin && isNotOnboarded;

  const navItems: NavItem[] = isSuperAdmin
    ? [
      {
        // Super admin remains intentionally isolated to Admin shell for now.
        label: 'Admin',
        icon: <DashboardIcon />,
        path: '/admin',
        roles: [ROLES.SUPER_ADMIN],
      },
    ]
    : shouldShowDashboardOnly
      ? [
        {
          label: 'Home',
          icon: <DashboardIcon />,
          path: '/dashboard',
          roles: [ROLES.PRIMARY, ROLES.PARTNER, ROLES.CHILD],
        },
      ]
      : [
      {
        label: 'Home',
        icon: <DashboardIcon />,
        path: '/dashboard',
        roles: [ROLES.PRIMARY, ROLES.PARTNER, ROLES.CHILD],
      },
      {
        label: 'Transactions',
        icon: <TransactionsIcon />,
        path: '/transactions',
        roles: [ROLES.PRIMARY, ROLES.PARTNER],
      },
      {
        label: 'Accounts',
        icon: <AccountBalanceWalletIcon />,
        path: '/accounts',
        roles: [ROLES.PRIMARY, ROLES.PARTNER],
      },
      { label: 'Calendar', icon: <CalendarMonthIcon />, path: '/calendar', roles: [ROLES.PRIMARY, ROLES.PARTNER] },
      { label: 'Budget', icon: <SavingsIcon />, path: '/budgets', roles: [ROLES.PRIMARY, ROLES.PARTNER] },
      { label: 'Milestones', icon: <MilestonesIcon />, path: '/milestones', roles: [ROLES.PRIMARY, ROLES.PARTNER, ROLES.CHILD] },
      { label: 'Reports', icon: <ReportsIcon />, path: '/reports', roles: [ROLES.PRIMARY] },
    ];


  const handleNav = (path: string) => {
    navigate(path);
    if (isMobile) {
      onMobileClose();
    }
  };

  const filteredItems = !userRole ? [] : navItems.filter((item) => item.roles.includes(userRole));

  const drawerWidth = collapsed ? 64 : 220;
  const mobileDrawerWidth = 220;
  const navCollapsed = !isMobile && collapsed;

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={isMobile ? onMobileClose : undefined}
      ModalProps={isMobile ? { keepMounted: true } : undefined}
      sx={{
        ...(isMobile
          ? {}
          : {
            width: drawerWidth,
            flexShrink: 0,
          }),
        '& .MuiDrawer-paper': {
          width: isMobile ? mobileDrawerWidth : drawerWidth,
          transition: 'width 0.3s',
          overflowX: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...(isMobile
            ? {
              top: 'var(--app-topbar-height, 64px)',
              height: 'calc(100% - var(--app-topbar-height, 64px))',
            }
            : {}),
        },
      }}
    >
      <Box>
        {!isMobile ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', padding: 1 }}>
              <IconButton
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                onClick={() => setCollapsed(!collapsed)}
              >
                {collapsed ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Box>
            <Divider />
          </>
        ) : null}
        <List>
          {filteredItems.map((item) => {
            const pathOnly = item.path.split('#')[0];
            const itemHash = item.path.includes('#') ? item.path.split('#')[1] : '';
            const currentHash = location.hash.startsWith('#') ? location.hash.slice(1) : '';
            const isActive =
              location.pathname === pathOnly && (!itemHash || currentHash === itemHash);

            return (
              <Tooltip title={navCollapsed ? item.label : ''} placement="right" key={item.path}>
                <ListItemButton
                  selected={isActive}
                  onClick={() => handleNav(item.path)}
                  sx={{
                    paddingLeft: navCollapsed ? 1.5 : item.indent ? 4 : 2,
                    paddingRight: navCollapsed ? 1.5 : 2,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: navCollapsed ? 0 : 2 }}>
                    {item.icon}
                  </ListItemIcon>
                  {!navCollapsed && <ListItemText primary={item.label} />}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>

      </Box>

      {/* Logout button pinned to bottom */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: 60, height: 60, mb: 1 }}>
          <img
            src="/assets/img/icon.png"
            alt="Velora logo"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>

        <Divider sx={{ width: '80%', my: 1 }} />

        <Tooltip title={navCollapsed ? 'Logout' : ''} placement="right">
          <ListItemButton
            onClick={() => {
              logout();
              if (isMobile) {
                onMobileClose();
              }
            }}
            sx={{
              paddingLeft: navCollapsed ? 1.5 : 2,
              paddingRight: navCollapsed ? 1.5 : 2,
              marginTop: 1,
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: navCollapsed ? 0 : 2 }}>
              <LogoutIcon />
            </ListItemIcon>
            {!navCollapsed && <ListItemText primary="Logout" />}
          </ListItemButton>
        </Tooltip>
      </Box>

    </Drawer>
  );
};

export default Sidebar;
